import translations from 'assets/locales/translations';
import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { userSelector } from 'store/reducers/usersSlice';
import { configThunks } from 'store/reducers/configSlice';
import { returnSelectors } from 'store/reducers/returnSlice';
import { decisionCodeThunks } from 'store/reducers/decisionCodeSlice';
import useAppSelector from './useAppSelector';
import useAppDispatch from './useAppDispatch';
import { setLanguage } from 'services/languageStore';
import { registerDatePickerLocale } from 'utils/datepickerLocalesConfig';

const STORAGE_KEY = 'preferredLanguage';
const availableLanguages = Object.keys(translations) as string[];

const languageLabels: Record<string, string> = {
    da: 'Dansk',
    de: 'Deutsch',
    en: 'English',
    es: 'Español',
    fi: 'Suomi',
    fr: 'Français',
    is: 'Íslenska',
    it: 'Italiano',
    nl: 'Nederlands',
    no: 'Norsk',
    pl: 'Polski',
    pt: 'Português',
    sl: 'Slovenščina',
    sv: 'Svenska',
};

const guessBrowserLanguage = (): string => {
    const lang = navigator.language.toLowerCase();
    const baseLang = lang.split('-')[0];
    return availableLanguages.includes(baseLang) ? baseLang : 'en';
};

export const useLanguage = () => {
    const userProfile = useAppSelector(userSelector.selectUserProfile);
    const customerDetails = useAppSelector(returnSelectors.selectCustomerDetails);
    const dispatch = useAppDispatch();
    const [lang, setLang] = useState<string>('en');

    useEffect(() => {
        const resolvedLang =
            userProfile?.preferredLanguage || localStorage.getItem(STORAGE_KEY) || guessBrowserLanguage();
        const baseLang = resolvedLang.split('-')[0];
        const finalLang = availableLanguages.includes(baseLang) ? baseLang : 'en';

        setLang(finalLang);
        i18next.changeLanguage(finalLang);
        localStorage.setItem(STORAGE_KEY, finalLang);
        setLanguage(finalLang);
        registerDatePickerLocale(finalLang);
    }, [userProfile]);

    const changeLanguage = (newLang: string) => {
        if (availableLanguages.includes(newLang)) {
            setLang(newLang);
            i18next.changeLanguage(newLang);
            localStorage.setItem(STORAGE_KEY, newLang);
            setLanguage(newLang);
            registerDatePickerLocale(newLang);
            dispatch(configThunks.getReturnCenterSettings());

            // Refresh decision codes to get updated translations
            if (customerDetails?.customerCode) {
                dispatch(decisionCodeThunks.getDecisionCodesForCustomer(customerDetails.customerCode.toString()));
            }
        }
    };

    const getLanguageLabel = (code: string) => {
        return languageLabels[code] ?? code;
    };

    return {
        lang,
        changeLanguage,
        availableLanguages,
        getLanguageLabel,
    };
};
